import { useEffect, useMemo, useState } from "react";
import { BookOpen, Plus, AlertCircle, RotateCw } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { useToast } from "@/components/ui/Toast";
import { listKnowledge, syncNow, type KnowledgeDoc, type KnowledgeSourceType } from "@/lib/api";
import { cn } from "@/lib/cn";
import { DocCard } from "./DocCard";
import { AddDocForm } from "./AddDocForm";

type Tab = "all" | KnowledgeSourceType;
type SourceFilter = "all" | "manual" | "synced";

const TAB_ORDER: Tab[] = [
  "all",
  "faq",
  "rates",
  "rules",
  "policies",
  "pet_policy",
  "cancellation_policy",
  "check_in_info",
  "reservation_info",
  "amenities",
  "description",
  "location",
  "other",
];

export const SOURCE_LABEL: Record<KnowledgeSourceType, string> = {
  rates: "Rates",
  rules: "Rules",
  amenities: "Amenities",
  policies: "Policies",
  cancellation_policy: "Cancellation",
  pet_policy: "Pet policy",
  check_in_info: "Check-in",
  reservation_info: "Reservations",
  description: "Description",
  location: "Location",
  faq: "FAQ",
  other: "Other",
};

/**
 * Knowledge base page. Lists every document grounding the bot — both
 * Roverpass-synced (is_manual=false, read-only) and owner-authored
 * (is_manual=true, deletable). Two filter dimensions: source (all / manual /
 * synced) and source_type (rates, rules, …). New entries are added via an
 * inline form; synced rows are display-only because the next Roverpass sync
 * would overwrite any edits.
 */
export function Knowledge() {
  const toast = useToast();
  const [tab, setTab] = useState<Tab>("all");
  const [source, setSource] = useState<SourceFilter>("all");
  const [docs, setDocs] = useState<KnowledgeDoc[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await listKnowledge();
      setDocs(r.items);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void refresh(); }, []);

  const onSync = async () => {
    setSyncing(true);
    try {
      const r = await syncNow();
      const changed = r.documents_added + r.documents_updated + r.documents_removed;
      toast.push(
        changed === 0
          ? "Up to date — no changes from Roverpass."
          : `Synced. ${changed} document${changed === 1 ? "" : "s"} updated from Roverpass.`,
      );
      await refresh();
    } catch (e) {
      toast.push(e instanceof Error ? e.message : "Sync failed", "error");
    } finally {
      setSyncing(false);
    }
  };

  const docsBySource = useMemo(() => {
    if (!docs) return docs;
    if (source === "manual") return docs.filter((d) => d.is_manual);
    if (source === "synced") return docs.filter((d) => !d.is_manual);
    return docs;
  }, [docs, source]);

  const countsByType = useMemo(() => {
    const m = new Map<KnowledgeSourceType, number>();
    for (const d of docsBySource ?? []) {
      m.set(d.source_type, (m.get(d.source_type) ?? 0) + 1);
    }
    return m;
  }, [docsBySource]);

  const sourceCounts = useMemo(() => {
    let manual = 0;
    let synced = 0;
    for (const d of docs ?? []) {
      if (d.is_manual) manual++; else synced++;
    }
    return { manual, synced, total: manual + synced };
  }, [docs]);

  const visibleTabs = useMemo(
    () => TAB_ORDER.filter((t) => t === "all" || (countsByType.get(t as KnowledgeSourceType) ?? 0) > 0 || t === "faq"),
    [countsByType],
  );

  const filtered = useMemo(() => {
    if (!docsBySource) return [];
    if (tab === "all") return docsBySource;
    return docsBySource.filter((d) => d.source_type === tab);
  }, [docsBySource, tab]);

  const onDocAdded = (doc: KnowledgeDoc) => {
    setDocs((prev) => (prev ? [doc, ...prev] : [doc]));
    setAdding(false);
    toast.push("Saved. Your bot already knows this.");
  };

  const onDocDeleted = (id: string) => {
    setDocs((prev) => (prev ? prev.filter((d) => d.id !== id) : prev));
    toast.push("Removed. Your bot will stop using it.");
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-medium text-text-strong">Knowledge base</h1>
          <p className="mt-1 text-sm text-text-soft">
            Everything your bot knows about your campground. Rates, rules and policies come straight from Roverpass; add anything else here. Changed something in Roverpass? Hit <span className="font-medium text-text-strong">Sync from Roverpass</span> to pull it in.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void onSync()}
            loading={syncing}
            iconLeft={!syncing && <RotateCw size={12} strokeWidth={1.75} />}
            title="Pull the latest rates, rules and amenities from Roverpass"
          >
            {syncing ? "Syncing…" : "Sync from Roverpass"}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setAdding((v) => !v)}
            iconLeft={<Plus size={12} strokeWidth={1.75} />}
          >
            {adding ? "Cancel" : "Add knowledge"}
          </Button>
        </div>
      </div>

      {adding && (
        <AddDocForm
          onCancel={() => setAdding(false)}
          onAdded={onDocAdded}
        />
      )}

      {/* Source filter (manual / synced / all) */}
      {docs && docs.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <SourcePill active={source === "all"} onClick={() => setSource("all")}>
            All <span className="ml-1 tabular-nums text-text-faint">{sourceCounts.total}</span>
          </SourcePill>
          <SourcePill active={source === "synced"} onClick={() => setSource("synced")}>
            From Roverpass <span className="ml-1 tabular-nums text-text-faint">{sourceCounts.synced}</span>
          </SourcePill>
          <SourcePill active={source === "manual"} onClick={() => setSource("manual")}>
            Manual <span className="ml-1 tabular-nums text-text-faint">{sourceCounts.manual}</span>
          </SourcePill>
        </div>
      )}

      {/* Source-type tab bar */}
      <div className="flex flex-wrap items-center gap-1 border-b border-line">
        {visibleTabs.map((t) => (
          <TabButton key={t} active={tab === t} onClick={() => setTab(t)}>
            {t === "all" ? "All" : SOURCE_LABEL[t as KnowledgeSourceType]}
            {t === "all"
              ? docsBySource && (
                <span className="ml-1.5 rounded-full bg-surface-3 px-1.5 py-0.5 text-2xs tabular-nums text-text-soft">
                  {docsBySource.length}
                </span>
              )
              : (countsByType.get(t as KnowledgeSourceType) ?? 0) > 0 && (
                <span className="ml-1.5 rounded-full bg-surface-3 px-1.5 py-0.5 text-2xs tabular-nums text-text-soft">
                  {countsByType.get(t as KnowledgeSourceType)}
                </span>
              )}
          </TabButton>
        ))}
      </div>

      {error && (
        <Card className="border-danger/30">
          <div className="flex items-start gap-3 px-5 py-4">
            <AlertCircle size={18} strokeWidth={1.75} className="mt-0.5 shrink-0 text-danger" />
            <div className="flex-1">
              <p className="text-sm font-medium text-text-strong">Couldn't load documents</p>
              <p className="mt-1 text-xs text-text-soft">{error}</p>
            </div>
          </div>
        </Card>
      )}

      {!error && loading && !docs && <DocsListSkeleton />}

      {!error && docs && filtered.length === 0 && (
        <Card>
          <div className="px-5 py-6">
            <EmptyState
              icon={BookOpen}
              title={tab === "all" ? "No documents yet" : `No ${SOURCE_LABEL[tab as KnowledgeSourceType].toLowerCase()} documents`}
              description={
                tab === "all"
                  ? "Add anything you'd want your bot to know — special events, local tips, custom policies."
                  : "Nothing here yet. Try another tab or add a new entry."
              }
              action={
                !adding && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setAdding(true)}
                    iconLeft={<Plus size={12} strokeWidth={1.75} />}
                  >
                    Add knowledge
                  </Button>
                )
              }
            />
          </div>
        </Card>
      )}

      {!error && docs && filtered.length > 0 && (
        <div className="space-y-3">
          {filtered.map((doc) => (
            <DocCard key={doc.id} doc={doc} onDeleted={onDocDeleted} />
          ))}
        </div>
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center border-b-2 px-4 py-2.5 text-sm transition-colors",
        active
          ? "border-coral text-text-strong"
          : "border-transparent text-text-soft hover:text-text-strong",
      )}
    >
      {children}
    </button>
  );
}

function SourcePill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center rounded-full border px-2.5 py-1 text-xs transition-colors",
        active
          ? "border-coral bg-coral-soft text-coral-ink"
          : "border-line bg-surface text-text-soft hover:bg-surface-2 hover:text-text-strong",
      )}
    >
      {children}
    </button>
  );
}

function DocsListSkeleton() {
  return (
    <div className="space-y-3">
      {[0, 1, 2].map((i) => (
        <div key={i} className="card">
          <div className="flex items-start justify-between gap-4 px-5 py-4">
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-3 w-1/3" />
            </div>
            <Skeleton className="h-8 w-20" />
          </div>
        </div>
      ))}
    </div>
  );
}
